import { useState } from "react";
import { motion } from "framer-motion";
import {
  WifiHigh,
  HouseLine,
  TelevisionSimple,
  SpeakerHifi,
  Wrench,
  Buildings,
  Printer,
  Devices,
  VideoCamera,
  Phone,
  Envelope,
  FacebookLogo,
  CheckCircle,
  MapPin,
  List,
  X,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_services-showcase-9/artifacts/nppvp6x6_8e663d20-a8e2-4269-96ef-9f9ebf55cfff.png";
const LOGO_DARK_URL = "https://customer-assets.emergentagent.com/job_services-showcase-9/artifacts/w8so4b1e_028b5638-0d14-4f92-a590-f8774051944e.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_services-showcase-9/artifacts/gqonzc6b_4086FAA8-746D-41C9-949C-5FA3104C2205.png";
const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_services-showcase-9/artifacts/iim3bf8f_FB5CB988-6246-469E-AA06-A86ED3F39F8D.png";

const services = [
  { icon: WifiHigh, title: "Wi-Fi & Internet Setup", desc: "Get connected with reliable home and business Wi-Fi configuration." },
  { icon: HouseLine, title: "Smart Home Device Setup", desc: "Connect and configure all your smart home devices seamlessly." },
  { icon: TelevisionSimple, title: "Smart TV & Streaming Setup", desc: "Set up your streaming services and smart TV for entertainment." },
  { icon: SpeakerHifi, title: "Home Theater Installation", desc: "Professional audio and video system installation for your home." },
  { icon: Wrench, title: "Computer Repair & Cleanup", desc: "Speed up slow computers and fix hardware or software issues." },
  { icon: Buildings, title: "Tech Help for Small Businesses", desc: "Comprehensive IT support tailored for small business needs." },
  { icon: Printer, title: "Printer Setup & Troubleshooting", desc: "Get your printers working and connected to all devices." },
  { icon: Devices, title: "Device Setup", desc: "Configure phones, tablets, and computers for optimal performance." },
];

const whyChoose = [
  { title: "Local & Reliable", desc: "Trusted by your neighbors in South Carolina." },
  { title: "Fast Tech Help", desc: "Quick response times when you need it most." },
  { title: "In-Home Service", desc: "We come to you for convenient support." },
  { title: "Small Business Support", desc: "Specialized solutions for local businesses." },
  { title: "Affordable Pricing", desc: "Quality service that fits your budget." },
  { title: "Easy To Understand Help", desc: "No confusing tech jargon, just clear solutions." },
];

const serviceAreas = ["Camden", "Lugoff", "Elgin", "Columbia", "Sumter"];

const navLinks = [
  { href: "#services", label: "Services" },
  { href: "#why-us", label: "Why Us" },
  { href: "#service-area", label: "Service Area" },
  { href: "#contact", label: "Contact" },
];

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_needed: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_needed: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", service_needed: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="#" data-testid="header-logo" className="flex items-center gap-2">
              <img 
                src={LOGO_URL} 
                alt="TES-Tech Solutions" 
                className="h-10 w-auto rounded"
              />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                  className="text-slate-600 hover:text-blue-600 transition-colors font-medium"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <a href="tel:8039101060">
                <Button data-testid="header-call-btn" className="bg-blue-600 hover:bg-blue-700 btn-lift">
                  <Phone className="mr-2 h-4 w-4" weight="bold" />
                  Call Now
                </Button>
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200">
            <nav className="flex flex-col px-6 py-4 gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                  className="text-slate-600 hover:text-blue-600 transition-colors font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a href="tel:8039101060">
                <Button data-testid="mobile-call-btn" className="w-full bg-blue-600 hover:bg-blue-700">
                  <Phone className="mr-2 h-4 w-4" weight="bold" />
                  Call Now
                </Button>
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-16 pb-20 md:pt-20 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Large Logo Display */}
          <motion.div 
            {...fadeInUp}
            className="flex justify-center mb-12 md:mb-16"
          >
            <img 
              src={LOGO_URL} 
              alt="TES-Tech Solutions" 
              className="h-28 sm:h-36 md:h-44 lg:h-52 w-auto object-contain rounded-2xl"
            />
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight">
                We Make Technology{" "}
                <span className="text-blue-600">Easy.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
                Don't Stress About Tech... Call TES-Tech! Home and small business
                tech support in South Carolina.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href="#contact">
                  <Button
                    data-testid="hero-book-btn"
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 btn-lift text-base px-8"
                  >
                    Book a Service
                  </Button>
                </a>
                <a href="#services">
                  <Button
                    data-testid="hero-services-btn"
                    size="lg"
                    variant="outline"
                    className="border-slate-300 hover:bg-slate-100 text-base px-8"
                  >
                    View Services
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMAGE}
                  alt="Smart home technology"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Services We Offer
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              From home Wi-Fi to business IT support, we've got you covered.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
                className="service-card bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-lg"
              >
                <div className="bg-blue-50 text-blue-600 p-3 rounded-lg w-fit mb-4">
                  <service.icon size={28} weight="duotone" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp} className="order-2 lg:order-1">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-8">
                Why Choose TES-Tech?
              </h2>

              <div className="grid sm:grid-cols-2 gap-6">
                {whyChoose.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    data-testid={`why-item-${index}`}
                    className="flex gap-3"
                  >
                    <CheckCircle
                      size={24}
                      weight="fill"
                      className="text-blue-600 flex-shrink-0 mt-0.5"
                    />
                    <div>
                      <h4 className="font-heading font-semibold text-slate-900">
                        {item.title}
                      </h4>
                      <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={ABOUT_IMAGE}
                  alt="Technician repairing computer"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section id="service-area" className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <MapPin size={48} weight="duotone" className="mx-auto text-blue-400 mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Proudly Serving South Carolina
            </h2>
            <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">
              We provide in-home tech support across these communities
            </p>

            <div className="flex flex-wrap justify-center gap-4 md:gap-8">
              {serviceAreas.map((area, index) => (
                <motion.span
                  key={area}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  data-testid={`service-area-${area.toLowerCase()}`}
                  className="px-6 py-3 bg-slate-800 rounded-lg text-lg font-medium border border-slate-700"
                >
                  {area}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Let's Get Your Tech Working.
              </h2>
              <p className="text-slate-600 text-lg mb-8 max-w-md">
                Ready to solve your tech problems? Reach out today and let us help.
              </p>

              <div className="space-y-6">
                <a
                  href="tel:8039101060"
                  data-testid="contact-phone"
                  className="flex items-center gap-4 group"
                >
                  <div className="bg-blue-50 text-blue-600 p-3 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <Phone size={24} weight="bold" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                      (803) 910-1060
                    </p>
                  </div>
                </a>

                <a
                  href="mailto:support@tes-tech.biz"
                  data-testid="contact-email"
                  className="flex items-center gap-4 group"
                >
                  <div className="bg-blue-50 text-blue-600 p-3 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <Envelope size={24} weight="bold" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                      support@tes-tech.biz
                    </p>
                  </div>
                </a>

                <a
                  href="https://www.facebook.com/share/1B2qZzairG/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook"
                  className="flex items-center gap-4 group"
                >
                  <div className="bg-blue-50 text-blue-600 p-3 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <FacebookLogo size={24} weight="bold" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Facebook</p>
                    <p className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                      TES-Tech Solutions
                    </p>
                  </div>
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeInUp} transition={{ duration: 0.5, delay: 0.2 }}>
              <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 shadow-lg">
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">
                  Send Us a Message
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-slate-700">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-form-name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      required
                      className="mt-1.5 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-700">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-form-email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                      className="mt-1.5 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-slate-700">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="contact-form-phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(803) 000-0000"
                      className="mt-1.5 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-slate-700">
                      Service Needed
                    </Label>
                    <Select
                      value={formData.service_needed}
                      onValueChange={handleServiceChange}
                    >
                      <SelectTrigger
                        data-testid="contact-form-service"
                        className="mt-1.5 border-slate-300"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.title} value={service.title}>
                            {service.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-form-message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="How can we help you?"
                      rows={4}
                      required
                      className="mt-1.5 border-slate-300 focus:border-blue-500 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-form-submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 btn-lift text-base py-5"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col items-center gap-8">
            <img src={LOGO_DARK_URL} alt="TES-Tech Solutions" className="h-20 md:h-24 w-auto" />
            
            <p className="text-slate-400 text-center italic text-lg">
              "Don't Stress About Tech... Call TES-Tech!"
            </p>

            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} TES-Tech Solutions. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
